import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import signalsLogo from '../assets/signals/signals-logo.png';
import investorIcon from '../assets/signals/investor-icon.png';
import startupIcon from '../assets/signals/startup-icon.png';

const STORAGE_KEY = 'prairiSignalsPopup';
const SESSION_SHOWN_KEY = 'prairiSignalsPopupShown';
/** After dismiss (without subscribe), allow showing again after 3 days. */
const DISMISS_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;

/** Figma: Green filtered / #6F9B41 */
const GREEN = '#6F9B41';
const GREEN_HOVER = '#5d8435';
const GREEN_30 = 'rgba(111, 155, 65, 0.3)';
const GREEN_15 = 'rgba(111, 155, 65, 0.15)';
/** Figma: Charcoal Black */
const PANEL_BG = '#262626';
/** Figma: White */
const TEXT = '#F2F2F2';
/** Figma muted white for unselected labels / placeholders */
const TEXT_MUTED = 'rgba(242, 242, 242, 0.7)';
const RADIUS = 10;

type SignalsRole = 'investor' | 'startup';
type Step = 'question' | 'reply';

const ROLE_REPLY: Record<SignalsRole, { punchline: string; detail: string }> = {
  investor: {
    punchline: 'One.',
    detail: 'Once three other VCs say the room has potential.',
  },
  startup: {
    punchline: 'One.',
    detail: 'After the founder explains why this light bulb is a $10B market.',
  },
};

/**
 * Show exit popup on public pages. Skip auth/signup funnels and member app
 * surfaces (logged-in users are already excluded via auth checks).
 */
function isPopupExcludedPath(pathname: string): boolean {
  const excludedExact = new Set([
    '/login',
    '/password-reset',
    '/unsubscribe',
    '/continue',
    '/verify-email',
    '/email-action',
    '/express-signup',
    '/test',
    '/builder-test',
    '/investors/pricing',
    '/startups/pricing',
  ]);
  if (excludedExact.has(pathname)) return true;

  const excludedPrefixes = [
    '/signup',
    '/finalstep',
    '/dashboard',
    '/favorites',
    '/inbox',
    '/settings',
    '/landing',
    '/profile',
    '/myprofile',
    '/admin',
    '/checkout',
  ];
  return excludedPrefixes.some(p => pathname === p || pathname.startsWith(`${p}/`));
}

/** Paid visitors should not see the signup popup again in this browser. */
export function suppressExitPopupAfterPayment(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'paid');
    sessionStorage.setItem(SESSION_SHOWN_KEY, '1');
  } catch {
    /* ignore */
  }
}

function hasStoredDecision(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return false;
    if (value === 'subscribed' || value === 'paid') return true;
    // Legacy permanent dismiss — treat as expired so 3-day cooldown applies going forward
    if (value === 'dismissed') return false;
    if (value.startsWith('dismissed:')) {
      const at = Number(value.slice('dismissed:'.length));
      if (!Number.isFinite(at)) return false;
      return Date.now() - at < DISMISS_COOLDOWN_MS;
    }
    return false;
  } catch {
    return true;
  }
}

function hasCookieConsent(): boolean {
  try {
    return Boolean(localStorage.getItem('cookieConsent'));
  } catch {
    return false;
  }
}

interface RoleOptionProps {
  role: SignalsRole;
  selected: boolean;
  onSelect: () => void;
  label: string;
  iconSrc: string;
}

const RoleOption: React.FC<RoleOptionProps> = ({ selected, onSelect, label, iconSrc }) => {
  const [hovered, setHovered] = useState(false);
  /** Figma 971-1243: 1px border, radio → icon → label */
  const active = selected || hovered;
  const border = active ? GREEN : TEXT;
  const labelColor = selected || hovered ? TEXT : TEXT_MUTED;
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-pressed={selected}
      className="flex flex-1 items-center justify-start gap-1 border px-3 py-3 text-left transition-colors sm:px-4 sm:py-3"
      style={{
        borderColor: border,
        borderWidth: 1,
        borderRadius: RADIUS,
        backgroundColor: selected ? GREEN_30 : hovered ? GREEN_15 : 'transparent',
        fontFamily: 'Rubik, sans-serif',
      }}
    >
      <span
        className="mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
        style={{ borderColor: active ? GREEN : TEXT, borderWidth: 1 }}
        aria-hidden="true"
      >
        {selected ? (
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: GREEN }} />
        ) : null}
      </span>
      <img
        src={iconSrc}
        alt=""
        className="h-[30px] w-[30px] shrink-0 object-contain"
        style={{ mixBlendMode: 'lighten' }}
      />
      <span
        className="text-[13px] font-normal leading-tight sm:whitespace-nowrap sm:text-[20px] sm:leading-none"
        style={{ color: labelColor }}
      >
        {label}
      </span>
    </button>
  );
};

const PrairiSignalsExitPopup: React.FC = () => {
  const { pathname } = useLocation();
  const { user, firebaseUser, loading: authLoading } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>('question');
  const [role, setRole] = useState<SignalsRole | null>(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cookieReady, setCookieReady] = useState(hasCookieConsent);

  useEffect(() => {
    if (cookieReady) return;
    const id = window.setInterval(() => {
      if (hasCookieConsent()) {
        setCookieReady(true);
        window.clearInterval(id);
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [cookieReady]);

  const eligible =
    cookieReady &&
    !authLoading &&
    !user &&
    !firebaseUser &&
    !isPopupExcludedPath(pathname) &&
    !hasStoredDecision();

  const persist = (value: 'dismissed' | 'subscribed') => {
    try {
      if (value === 'subscribed') {
        localStorage.setItem(STORAGE_KEY, 'subscribed');
      } else {
        localStorage.setItem(STORAGE_KEY, `dismissed:${Date.now()}`);
      }
    } catch {
      /* ignore */
    }
  };

  const dismiss = () => {
    persist('dismissed');
    setIsOpen(false);
  };

  useEffect(() => {
    const forcePreview =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('signalsPopup') === '1';

    if ((!eligible && !forcePreview) || isOpen || success) return;

    let shown = false;
    try {
      shown = sessionStorage.getItem(SESSION_SHOWN_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (shown && !forcePreview) return;

    const openPopup = () => {
      if (!forcePreview && hasStoredDecision()) return;
      try {
        if (!forcePreview) {
          if (sessionStorage.getItem(SESSION_SHOWN_KEY) === '1') return;
          sessionStorage.setItem(SESSION_SHOWN_KEY, '1');
        }
      } catch {
        /* ignore */
      }
      setStep('question');
      setRole(null);
      setEmail('');
      setError('');
      setSuccess(false);
      setIsOpen(true);
    };

    if (forcePreview) {
      openPopup();
      return;
    }

    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY > 0) return;
      if (e.relatedTarget != null) return;
      openPopup();
    };

    const timer = window.setTimeout(openPopup, 45000);

    document.addEventListener('mouseout', onMouseOut);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.clearTimeout(timer);
    };
  }, [eligible, isOpen, success]);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleTellAnswer = () => {
    if (!role) return;
    setStep('reply');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!role) {
      setError('Please choose Investor or Startup Founder.');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const subscribe = httpsCallable(functions, 'subscribePrairiSignals');
      await subscribe({ email: email.trim(), role });
      persist('subscribed');
      setSuccess(true);
      window.setTimeout(() => setIsOpen(false), 1600);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Something went wrong. Please try again.';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99990] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={e => {
        if (e.target === e.currentTarget) dismiss();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="prairi-signals-title"
    >
      <div
        className="relative w-full max-w-[580px] overflow-hidden rounded-[32px] border sm:max-w-[680px] sm:rounded-[48px]"
        style={{
          backgroundColor: PANEL_BG,
          borderColor: GREEN,
          borderWidth: 1,
          fontFamily: 'Rubik, sans-serif',
          color: TEXT,
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-4 top-3 z-10 p-1 text-2xl font-semibold leading-none sm:right-8 sm:top-6"
          style={{ color: TEXT }}
          aria-label="Close"
        >
          ×
        </button>

        <div className="flex flex-col gap-4 px-5 pb-5 pt-5 sm:gap-4 sm:px-10 sm:pb-10 sm:pt-10">
          {success ? (
            <div className="py-8 text-center">
              <p
                className="mb-2 text-sm font-medium uppercase tracking-wide"
                style={{ color: GREEN }}
              >
                You&apos;re on the list
              </p>
              <p className="text-xl font-semibold sm:text-2xl">
                Thanks — watch your inbox for PRAIRI Signals.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 pr-8">
                <img
                  src={signalsLogo}
                  alt=""
                  className="h-8 w-8 object-contain sm:h-[50px] sm:w-[50px]"
                  style={{ mixBlendMode: 'lighten' }}
                />
                <p
                  className="text-[17px] font-semibold tracking-[-0.01em] sm:text-2xl"
                  style={{ color: GREEN }}
                >
                  PRAIRI SIGNALS
                </p>
              </div>

              {step === 'question' ? (
                <>
                  <h2
                    id="prairi-signals-title"
                    className="px-4 py-4 text-center text-[20px] font-semibold leading-snug sm:px-6 sm:py-6 sm:text-[32px] sm:leading-tight"
                    style={{ backgroundColor: GREEN_30, color: TEXT, borderRadius: 20 }}
                  >
                    How many VCs does it take to change a light bulb?
                  </h2>

                  {/* Figma 971-1243: large gap before role prompt on desktop */}
                  <div className="h-8 shrink-0 sm:h-20" aria-hidden="true" />

                  <p
                    className="text-center text-[18px] font-light sm:text-2xl"
                    style={{ color: TEXT }}
                  >
                    Choose your role for your tailored reply
                  </p>

                  <div className="flex flex-row gap-3 sm:gap-4">
                    <RoleOption
                      role="investor"
                      label="Investor"
                      iconSrc={investorIcon}
                      selected={role === 'investor'}
                      onSelect={() => setRole('investor')}
                    />
                    <RoleOption
                      role="startup"
                      label="Startup Founder"
                      iconSrc={startupIcon}
                      selected={role === 'startup'}
                      onSelect={() => setRole('startup')}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleTellAnswer}
                    disabled={!role}
                    className="signals-cta mt-1 flex w-full items-center justify-center px-5 py-3 text-center text-[16px] font-normal transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:mx-auto sm:mt-2 sm:w-1/2 sm:text-2xl"
                    style={{ backgroundColor: GREEN, color: TEXT, borderRadius: RADIUS }}
                    onMouseEnter={e => {
                      if (!role) return;
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_HOVER;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN;
                    }}
                  >
                    Show me the answer
                  </button>
                </>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-left">
                  <h2
                    id="prairi-signals-title"
                    className="px-4 py-4 text-center font-semibold sm:px-6 sm:py-6"
                    style={{ backgroundColor: GREEN_30, color: TEXT, borderRadius: 20 }}
                  >
                    <span className="block text-[22px] leading-none sm:text-[32px]">
                      {role ? ROLE_REPLY[role].punchline : 'One.'}
                    </span>
                    <span className="mt-1.5 block text-[15px] font-semibold leading-snug sm:mt-2 sm:text-[18px]">
                      {role
                        ? ROLE_REPLY[role].detail
                        : 'Once three other VCs say the room has potential.'}
                    </span>
                  </h2>

                  <div>
                    <p className="text-[17px] font-light sm:text-[22px]" style={{ color: TEXT }}>
                      But seriously...
                    </p>
                    <p
                      className="mt-1.5 max-w-[34em] text-[16px] font-light leading-snug sm:mt-2 sm:text-[22px] sm:leading-[1.25]"
                      style={{ color: TEXT }}
                    >
                      Sign up to our emails and profit from our{' '}
                      <span className="font-medium" style={{ color: GREEN }}>
                        most sought-after
                      </span>{' '}
                      founder and investor data - and for FREE.
                    </p>
                  </div>

                  <p className="mb-0 text-[15px] font-light sm:text-[17px]" style={{ color: TEXT }}>
                    Choose your role for tailored updates
                  </p>

                  <div className="flex flex-row gap-3 sm:gap-4">
                    <RoleOption
                      role="investor"
                      label="Investor"
                      iconSrc={investorIcon}
                      selected={role === 'investor'}
                      onSelect={() => setRole('investor')}
                    />
                    <RoleOption
                      role="startup"
                      label="Startup Founder"
                      iconSrc={startupIcon}
                      selected={role === 'startup'}
                      onSelect={() => setRole('startup')}
                    />
                  </div>

                  <label htmlFor="prairi-signals-email" className="sr-only">
                    Your email
                  </label>
                  <input
                    id="prairi-signals-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="Your email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="h-auto w-full border bg-transparent px-4 py-3 text-left text-[15px] font-normal outline-none placeholder:text-[rgba(242,242,242,0.7)] focus:border-[#6F9B41] sm:text-xl"
                    style={{
                      borderColor: TEXT,
                      borderWidth: 1,
                      borderRadius: RADIUS,
                      color: TEXT,
                    }}
                  />

                  {error ? <p className="-mt-2 text-sm text-red-400">{error}</p> : null}

                  <button
                    type="submit"
                    disabled={submitting || !email.trim() || !role}
                    className="flex w-full items-center px-4 py-3 text-left text-[15px] font-normal transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:text-xl"
                    style={{ backgroundColor: GREEN, color: TEXT, borderRadius: RADIUS }}
                    onMouseEnter={e => {
                      if (submitting || !email.trim() || !role) return;
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_HOVER;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN;
                    }}
                  >
                    {submitting ? 'Submitting…' : 'Sign me up'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrairiSignalsExitPopup;
